import { DateService } from '@rocheglobal/component-library-dot-com/src/services/date';
import { MediaItem, PubMedItem } from './types';
import { getLocalizedDetailedPagePath } from '../../affilate-logic/roche-ch';
import { ExternalDataService } from '../../services/external-data';
import { EventsCategory, NewsCategory, RocheEndpoint } from '../../services/external-data/types';
import { LanguageService } from '../../services/language';
import { parseCludoEngineID } from '../../utils/parse-cludo-engine';

const config = {
  MEDIA: {
    path: 'media/releases',
  },
  INVESTOR: {
    path: 'investors/updates',
  },
};

export async function fetchPubMedTabs() {
  const categories = await ExternalDataService.getPubMedCategories();

  return ['All', ...categories.functions];
}

export async function fetchPubMedList({ tab, page }) {
  const data = await ExternalDataService.getPubMed({ page: page - 1, size: 10 }, '', tab === 'All' ? '' : tab);

  const pubs: RocheEndpoint<PubMedItem[]> = {
    Page: page,
    Total: data.rowCount,
    Items: data.publications,
  };

  for await (const [index, item] of pubs.Items.entries()) {
    pubs.Items[index] = {
      ...item,
      parsedDate: await DateService.formatDate(item?.publishingDate, 'dateFull'),
    };
  }

  return pubs;
}

export async function fetchPubMedSearch({ tab, query, page }) {
  const data = await ExternalDataService.getPubMed({ page: page - 1, size: 10 }, query, tab === 'All' ? '' : tab);

  const pubs: RocheEndpoint<PubMedItem[]> = {
    Page: page,
    Total: data.rowCount,
    Items: data.publications,
  };

  for await (const [index, item] of pubs.Items.entries()) {
    pubs.Items[index] = {
      ...item,
      parsedDate: await DateService.formatDate(item?.publishingDate, 'dateFull'),
    };
  }

  return pubs;
}

export async function fetchInvestorEventsTabs() {
  const events = await ExternalDataService.getEventYears({
    category: EventsCategory.INVESTOR,
  });

  events.unshift('upcoming');

  return events;
}

export async function fetchInvestorEventsList({ tab, page }) {
  return fetchEventsList({ tab, page, category: EventsCategory.INVESTOR });
}

export async function fetchEventsTabs() {
  const events = await ExternalDataService.getEventYears({
    category: EventsCategory.MEDIA,
  });

  events.unshift('upcoming');

  return events;
}

export async function fetchEventsList({ tab, page, category = EventsCategory.MEDIA }) {
  const events =
    tab === 'upcoming'
      ? await ExternalDataService.getUpcomingEvents({ category, page })
      : await ExternalDataService.getEvents({ category, year: tab, page });

  for await (const [index, item] of events.Items.entries()) {
    events.Items[index] = {
      ...item,
      startDate: await DateService.formatDate(item['Start Date'] as string | Date, 'dateEvents'),
    };
  }

  return events;
}

export function fetchSwissNewsTabs() {
  return ExternalDataService.getNewsYears({ category: NewsCategory.SWISS });
}

export function fetchSwissNewsList(args) {
  return fetchNewsList({ ...args, category: NewsCategory.SWISS });
}

export function fetchInvestorUpdatesList(args) {
  return fetchNewsList({ ...args, category: NewsCategory.INVESTOR });
}

export function fetchInvestorUpdatesSearch(args) {
  return fetchNewsSearch({ ...args, type: 'investors' });
}

export function fetchNewsTabs() {
  return ExternalDataService.getNewsYears({ category: NewsCategory.MEDIA });
}

export function fetchInvestorTabs() {
  return ExternalDataService.getNewsYears({ category: NewsCategory.INVESTOR });
}

export async function fetchNewsList({ tab, page, category = NewsCategory.MEDIA }) {
  const result = await ExternalDataService.getNews({ category, year: tab, page });
  result.Items = await extendNewsItemsData(result.Items);

  return result;
}

export async function fetchNewsSearch({ query, page, type = NewsCategory.MEDIA }) {
  const locale = LanguageService.getActiveLanguage();
  const websiteName = process.env.GATSBY_WEBSITE_NAME;
  const cludoFuzziness = process.env.GATSBY_CLUDO_FUZZINESS;
  const cludoCustomerId = process.env.GATSBY_CLUDO_CUSTOMER_ID;
  const cludoEngineId =
    process.env.GATSBY_CLUDO_ENGINE_ID || parseCludoEngineID(process.env.GATSBY_CLUDO_ENGINE_ID_LIST, locale);

  const results = await fetch(`https://api.cludo.com/api/v3/${cludoCustomerId}/${cludoEngineId}/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `SiteKey ${btoa(`${cludoCustomerId}:${cludoEngineId}:SiteKey`)}`,
    },
    body: JSON.stringify({
      query: cludoFuzziness ? `${query}` : `${query}~`,
      perPage: 10,
      page,
      filters: { Type: [type], WebsiteName: [websiteName], Lang: [locale] },
      enableFacetFiltering: true,
      sort: {
        Date: 'desc',
      },
    }),
  }).then((response) => response.json());

  // transform cludo response to match the news list response
  const transformedResults: RocheEndpoint<unknown[]> = {
    Total: results.TotalDocument,
    Items: [],
    Page: page,
  };

  for await (const [index, item] of results.TypedDocuments.entries()) {
    const ReleaseDateTime = DateService.convertDateToTimeZone(
      item.Fields?.Date?.Value as string | Date,
      'Europe/Berlin',
    );
    const formattedReleaseDate = await DateService.formatDate(ReleaseDateTime as string | Date, 'dateFull');
    transformedResults.Items[index] = {
      localisedTitle: item.Fields?.Title?.Value,
      formattedReleaseDate,
      link: item.Fields?.Url?.Value,
    };
  }

  return transformedResults;
}

export function fetchAdhocTabs() {
  return ExternalDataService.getAdhocYears();
}

export async function fetchAdhocList({ tab, page }) {
  const result = await ExternalDataService.getAdhoc({ year: tab, page });
  result.Items = await extendNewsItemsData(result.Items);

  return result;
}

async function extendNewsItemsData(items: MediaItem[]) {
  const locale = LanguageService.getActiveLanguage();
  const extendedItems = [];

  for await (const [index, item] of items.entries()) {
    const ReleaseDateTime = DateService.convertDateToTimeZone(item.ReleaseDateTime as string | Date, 'Europe/Berlin');
    const formattedReleaseDate = await DateService.formatDate(ReleaseDateTime as string | Date, 'dateFull');

    let link = '';
    let path = '';

    if (item.Category !== 'SWISS') {
      link = LanguageService.getLocaleAwareLink(`${config[item.Category].path}/${item.Slug}`, locale);
      path = `${config[item.Category].path}`;
    } else {
      link = LanguageService.getLocaleAwareLink(
        `/${getLocalizedDetailedPagePath(locale)}/informationen/${item.Slug}`,
        locale,
      );
    }

    extendedItems[index] = {
      ...item,
      localisedTitle: ExternalDataService.getLocalisedValue(
        item as unknown as Record<string, unknown>,
        'Title',
        locale,
      ),
      formattedReleaseDate,
      link,
      path,
    };
  }

  return extendedItems;
}
